import { useEffect, useRef } from 'react';

/**
 * Move focus to an element whenever `key` changes.
 *
 * The client-side routers in these apps replace the page without the browser's
 * usual focus reset, so after every `navigate()` focus is left on a node that no
 * longer exists and falls back to `<body>` — a keyboard user restarts from the
 * top of the document and a screen-reader user hears nothing (WCAG 2.4.3).
 *
 * Pass the route key (or the stepper stage, or a dialog's open state) and the id
 * of the element that should receive focus. The target needs `tabIndex={-1}` so
 * it is programmatically focusable without joining the tab order; the shared
 * `focusVisibleBaseline` suppresses the ring on such targets so this does not
 * paint a stray outline.
 *
 * The first render is skipped deliberately: on a fresh page load the browser has
 * already put focus in the right place, and stealing it would break deep links.
 */
export function useFocusOnChange(key: string, targetId: string): void {
  const previous = useRef<string | null>(null);

  useEffect(() => {
    if (previous.current === null) {
      previous.current = key;
      return;
    }
    if (previous.current === key) return;
    previous.current = key;

    // Focus after paint so the new route's content is mounted.
    const frame = requestAnimationFrame(() => {
      document.getElementById(targetId)?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [key, targetId]);
}
